/** @format */
import { Injectable } from '@angular/core';
import * as _ from 'lodash-es';
import { DowngradedService, ServiceDowngradeMappings, StaticMembers } from 'sis-common';

/**
 * Owns the student-app sessionStorage façade for transient UI state — filter selections, expanded
 * panel ids, and similar per-user-per-session preferences. AngularJS consumers (planSeeker.js,
 * uiContext.js, completionMethods.component.js) keep injecting `uiStateStore` by name and resolve
 * to this service via the {@link DowngradedService} mapping below, so the AngularJS module name
 * `student.common.utils.uiStateStore` stays resolvable for legacy module-dep arrays without a
 * separate AngularJS factory file. The semantics — JSON encoding, null/undefined → removeItem,
 * defaultValue fallback on missing or unparseable values — match the AngularJS factory byte for byte.
 */
@StaticMembers<DowngradedService>()
@Injectable({
    providedIn: 'root',
})
export class UiStateStoreService {
    static downgrade: ServiceDowngradeMappings = {
        dependencies: [],
        moduleName: 'student.common.utils.uiStateStore',
        serviceName: 'uiStateStore',
    };

    storeField(prefix: string, key: string, value: unknown): void {
        const storageKey = prefix + key;
        if (value !== undefined && value !== null) {
            sessionStorage.setItem(storageKey, JSON.stringify(value));
        } else {
            sessionStorage.removeItem(storageKey);
        }
    }

    readField<T = unknown>(prefix: string, key: string, defaultValue?: T | null): T | null {
        const storageKey = prefix + key;
        const json = sessionStorage.getItem(storageKey);

        if (json !== null && json !== undefined) {
            try {
                return JSON.parse(json) as T;
            } catch (e) {
                console.error(`Error parsing session storage item for key ${storageKey}:`, e);
            }
        }
        return defaultValue || null;
    }

    readAndSetField<T = unknown>(prefix: string, key: string, target: object, defaultValue?: T | null): void {
        const value = this.readField<T>(prefix, key, defaultValue);
        _.set(target, key, value);
    }
}
